import update from 'immutability-helper';
import React, { createContext, useReducer, useContext, useRef } from 'react';
import usePage from '@dashboard/hooks/usePage';

const TicketGroupsContext = createContext();

function ticketGroupsReducer(state, action) {
  switch (action.type) {
    case 'add':
      if (state && state.length > 0) {
        const newIndex = action.index || state.length;

        return update(state, {
          // add new ticket group at the specified index
          $splice: [[newIndex, 0, ...action.values]],
        });
      } else {
        return action.values;
      }
    case 'set': {
      return update(state, {
        $set: action.values,
      });
    }
    case 'update': {
      const index = state.findIndex(ticketGroup => ticketGroup._id === action.ticketGroupId);

      return update(state, {
        [index]: {
          $set: action.values,
        },
      });
    }
    case 'addTicketType': {
      const ticketGroupIndex = state.findIndex(
        ticketGroup => ticketGroup._id === action.ticketGroupId,
      );

      if (action.index) {
        return update(state, {
          [ticketGroupIndex]: {
            ticketTypes: {
              $splice: [[action.index, 0, action.values]],
            },
          },
        });
      } else {
        return update(state, {
          [ticketGroupIndex]: {
            ticketTypes: {
              $push: [action.values],
            },
          },
        });
      }
    }
    case 'updateTicketType': {
      const ticketGroupIndex = state.findIndex(
        ticketGroup => ticketGroup._id === action.ticketGroupId,
      );

      const ticketTypeIndex = state[ticketGroupIndex].ticketTypes.findIndex(
        ticketType => ticketType._id === action.ticketTypeId,
      );

      return update(state, {
        [ticketGroupIndex]: {
          ticketTypes: {
            [ticketTypeIndex]: {
              $set: action.values,
            },
          },
        },
      });
    }
    case 'deleteTicketType': {
      const ticketGroupIndex = state.findIndex(
        ticketGroup => ticketGroup._id === action.ticketGroupId,
      );

      const ticketTypeIndex = state[ticketGroupIndex].ticketTypes.findIndex(
        ticketType => ticketType._id === action.ticketTypeId,
      );

      return update(state, {
        [ticketGroupIndex]: {
          ticketTypes: {
            $splice: [[ticketTypeIndex, 1]],
          },
        },
      });
    }
    case 'reorderTicketTypes': {
      const { ticketGroupId, ticketTypeIds } = action;
      const ticketGroup = state.find(t => t._id === ticketGroupId);

      if (!ticketGroup) return state;

      return update(state, {
        [ticketGroupId]: {
          $set: {
            ...ticketGroup,
            ticketTypes: ticketGroup.ticketTypes.sort(
              (a, b) => ticketTypeIds.indexOf(a._id) - ticketTypeIds.indexOf(b._id),
            ),
          },
        },
      });
    }
    case 'delete': {
      return state.filter(f => f._id !== action.ticketGroup._id);
    }
    default:
      return state;
  }
}

export const TicketGroupsProvider = ({ children }) => {
  const { page } = usePage();

  const [state, dispatch] = useReducer(ticketGroupsReducer, page?.ticketGroups || []);

  const prevTicketGroupsRef = useRef(page?.ticketGroups);
  if (page?.ticketGroups !== prevTicketGroupsRef.current) {
    prevTicketGroupsRef.current = page?.ticketGroups;
    dispatch({ type: 'set', values: page?.ticketGroups || [] });
  }

  return (
    <TicketGroupsContext.Provider value={{ state, dispatch }}>
      {children}
    </TicketGroupsContext.Provider>
  );
};

export const useTicketGroups = () => useContext(TicketGroupsContext);
